import {
  ADD_ON_REQUIRED_PLANS,
  PLAN_REF_ID_MAPPING,
  PLANS_IN_BUNDLE,
} from "@/data/plans";
import { createSubject } from "@/lib/observer";
import { TBillingInterval } from "@/types/app";
import {
  getIntervalPrice,
  getIntervalStrikePrice,
} from "@/utils/billingInterval";
import { TCheckoutSummaryData } from "../Checkout/handlers";
import {
  TCartItem,
  TCartServiceState,
  TCatalogAddOn,
  TCatalogPlan,
  TCatalogState,
  TCreditState,
  TPromoState,
  TSummaryState,
} from "./types.cart";
import { createBundleItem } from "./utils";

// --- Service ---

/**
 * Singleton store for cart state, built on a plain observer pattern
 * (`createSubject`) so it works outside of React and can be read synchronously
 * via `useSyncExternalStore` without a context provider.
 *
 * All mutations are synchronous and call `notify()` at the end so every
 * subscriber (React or otherwise) sees the update in the same tick.
 */
function createCartService() {
  let _cart: TCartItem[] = [];
  let _catalog: TCatalogState = { plans: [], addOns: [] };

  let _promo: TPromoState = {
    appliedCodes: [],
    inputValue: "",
    isApplying: false,
  };

  let _credit: TCreditState = {
    amount: 0,
    isUsed: false,
  };

  let _summary: TSummaryState = {
    data: undefined,
    loading: false,
    error: false,
  };

  /**
   * Snapshot of individual plans that were in the cart before they were
   * replaced by the bundle item. Kept so we can restore them if the bundle
   * is later removed.
   */
  let _preBundlePlans: TCartItem[] = [];

  /**
   * Product ref IDs of plans the user already owns (outside the cart).
   * Used by bundle-promotion logic so that owning e.g. culling + adding
   * editing + retouching still triggers auto-promotion to the bundle.
   */
  let _ownedPlanIds: string[] = [];

  /**
   * One-shot flag. When `true`, the next call to `shouldSkipNextSummaryFetch`
   * returns `true` and resets the flag — used to suppress the summary refetch
   * that would otherwise fire immediately after a promo code is applied
   * (since applying a code already triggers its own fetch).
   */
  let _skipNextFetch = false;
  let _cachedState: TCartServiceState | undefined = undefined;

  const subject = createSubject<TCartServiceState>();

  const buildState = (): TCartServiceState => ({
    cart: _cart,
    catalog: _catalog,
    promo: _promo,
    credit: _credit,
    summary: _summary,
  });

  const notify = () => {
    _cachedState = buildState();
    subject.notifyObservers(_cachedState);
  };

  // --- Cart ---

  /**
   * Adds an item to the cart with automatic bundle promotion/demotion:
   *
   * - **Add-on already in cart** → increments quantity instead of adding a duplicate.
   * - **Bundle item added directly** → saves the current individual plans to
   *   `_preBundlePlans` and removes them so the bundle replaces them cleanly.
   * - **Individual plan added while bundle is in cart** → silently ignored;
   *   the bundle already covers it.
   * - **All bundleable plans now in cart** → auto-promotes them to the bundle
   *   item (`createBundleItem`) and saves the displaced plans to `_preBundlePlans`.
   */
  const addItem = (item: TCartItem) => {
    const existing = _cart.find((i) => i.id === item.id);
    if (existing) {
      if ("type" in item && item.type !== "plan")
        updateItemQuantity(item.id as string, item.quantity ?? 1);
      return;
    }

    if ("type" in item && item.type === "plan") {
      const bundleId: string = PLAN_REF_ID_MAPPING.all;
      const isBundle = item.id === bundleId;
      const isIndividual = (PLANS_IN_BUNDLE as readonly string[]).includes(
        item.id as string,
      );
      const bundleInCart = _cart.some((i) => i.id === bundleId);

      if (isBundle) {
        _preBundlePlans = _cart.filter((i) =>
          (PLANS_IN_BUNDLE as readonly string[]).includes(i.id as string),
        );
        _cart = _cart.filter(
          (i) =>
            !(PLANS_IN_BUNDLE as readonly string[]).includes(i.id as string),
        );
        _cart = [..._cart, item];
        notify();
        return;
      }

      if (isIndividual && bundleInCart) return;

      _cart = [..._cart, item];

      const cartPlanIds = _cart
        .filter((i) =>
          (PLANS_IN_BUNDLE as readonly string[]).includes(i.id as string),
        )
        .map((i) => i.id as string);

      const coveredPlanIds = new Set([...cartPlanIds, ..._ownedPlanIds]);
      const hasAllThree = (PLANS_IN_BUNDLE as readonly string[]).every((id) =>
        coveredPlanIds.has(id),
      );

      if (hasAllThree) {
        _preBundlePlans = _cart.filter(
          (i) =>
            (PLANS_IN_BUNDLE as readonly string[]).includes(i.id as string) &&
            i.id !== item.id,
        );
        _cart = _cart.filter(
          (i) =>
            !(PLANS_IN_BUNDLE as readonly string[]).includes(i.id as string),
        );
        const bundleItem = createBundleItem(_catalog.plans, item.interval);
        if (bundleItem) _cart = [..._cart, bundleItem];
      }

      notify();
      return;
    }

    _cart = [..._cart, item];
    notify();
  };

  /**
   * Drops any add-ons whose required plan is no longer in the cart.
   * Called after every `removeItem` so orphaned add-ons can't linger.
   * Add-ons with no required-plan constraint are always kept.
   */
  const cascadeRemoveDependentAddOns = (cart: TCartItem[]): TCartItem[] => {
    const planIds = new Set(
      cart.filter((i) => i.type === "plan").map((i) => i.id as string),
    );
    return cart.filter((i) => {
      if (i.type === "plan") return true;
      const required = ADD_ON_REQUIRED_PLANS[i.id as string];
      if (!required || required.length === 0) return true;
      return required.some((planId) => planIds.has(planId));
    });
  };

  /**
   * Removes an item from the cart and runs `cascadeRemoveDependentAddOns`.
   *
   * Special case: removing the bundle item restores the individual plans that
   * were saved in `_preBundlePlans` before the bundle replaced them, so the
   * user doesn't lose their previous selection.
   *
   * @returns The removed item, or `null` if it wasn't in the cart.
   */
  const removeItem = (itemId: string): TCartItem | null => {
    const item = _cart.find((i) => i.id === itemId);
    if (!item) return null;

    if (
      itemId === (PLAN_REF_ID_MAPPING.all as string) &&
      _preBundlePlans.length > 0
    ) {
      _cart = _cart.filter((i) => i.id !== itemId);
      _cart = [..._cart, ..._preBundlePlans];
      _preBundlePlans = [];
      _cart = cascadeRemoveDependentAddOns(_cart);
      notify();
      return item;
    }
    _cart = _cart.filter((i) => i.id !== itemId);
    _cart = cascadeRemoveDependentAddOns(_cart);
    notify();
    return item;
  };

  /** Adjusts the quantity of an add-on by `delta`. Minimum quantity is 1. */
  const updateItemQuantity = (itemId: string, delta: number) => {
    _cart = _cart.map((i) =>
      i.id === itemId
        ? { ...i, quantity: Math.max(1, (i.quantity ?? 1) + delta) }
        : i,
    );
    notify();
  };

  /**
   * Replaces the plan items in the cart with `plans` while preserving any
   * existing add-ons. Clears `_preBundlePlans` since this is a full reset of
   * the plan selection (e.g. when syncing from an external source).
   */
  const updateCart = (plans: TCartItem[]) => {
    const addOns = _cart.filter((i) => i.type !== "plan");
    _cart = [...addOns, ...plans];
    _preBundlePlans = [];
    notify();
  };

  const clearCart = () => {
    _cart = [];
    _preBundlePlans = [];
    notify();
  };

  const setOwnedPlanIds = (ids: string[]) => {
    _ownedPlanIds = ids;
  };

  const reset = () => {
    _cart = [];
    _preBundlePlans = [];
    _ownedPlanIds = [];
    _promo = { appliedCodes: [], inputValue: "", isApplying: false };
    _summary = {
      data: undefined,
      loading: false,
      error: false,
    };
    _cachedState = undefined;
    _credit = {
      amount: 0,
      isUsed: false,
    };
    notify();
    console.log("CartService has been reset");
  };

  // --- Catalog ---

  /** Bulk-sets the full catalog. Replaces any existing plans and add-ons. */
  const setCatalog = (plans: TCatalogPlan[], addOns: TCatalogAddOn[]) => {
    _catalog = { plans, addOns };
    notify();
  };

  /**
   * Updates every cart item's `interval` and `price` to match the chosen
   * billing period, looking up the canonical price from the catalog.
   * Items not found in the catalog are left unchanged.
   */
  const repriceCart = (interval: TBillingInterval) => {
    _cart = _cart.map((item) => {
      const catalogEntry =
        _catalog.plans.find((p) => p.id === item.id) ??
        _catalog.addOns.find((a) => a.id === item.id);
      if (!catalogEntry) return item;
      return {
        ...item,
        interval,
        price: getIntervalPrice(catalogEntry.pricing, interval),
        listPrice: getIntervalStrikePrice(catalogEntry.pricing, interval),
      };
    });
    notify();
  };

  const addCatalogPlan = (plan: TCatalogPlan) => {
    if (_catalog.plans.some((p) => p.id === plan.id)) return;
    _catalog = { ..._catalog, plans: [..._catalog.plans, plan] };
    notify();
  };

  const removeCatalogPlan = (id: string) => {
    _catalog = {
      ..._catalog,
      plans: _catalog.plans.filter((p) => p.id !== id),
    };
    notify();
  };

  const addCatalogAddOn = (addOn: TCatalogAddOn) => {
    if (_catalog.addOns.some((a) => a.id === addOn.id)) return;
    _catalog = { ..._catalog, addOns: [..._catalog.addOns, addOn] };
    notify();
  };

  const removeCatalogAddOn = (id: string) => {
    _catalog = {
      ..._catalog,
      addOns: _catalog.addOns.filter((a) => a.id !== id),
    };
    notify();
  };

  // --- Promo ---

  const setPromoInput = (value: string) => {
    _promo = { ..._promo, inputValue: value };
    notify();
  };

  const applyPromoCode = (code: string) => {
    if (!code || _promo.appliedCodes.includes(code)) return;
    _promo = {
      ..._promo,
      appliedCodes: [..._promo.appliedCodes, code],
      inputValue: "",
    };
    notify();
  };

  const removePromoCode = (code: string) => {
    _promo = {
      ..._promo,
      appliedCodes: _promo.appliedCodes.filter((c) => c !== code),
    };
    notify();
  };

  const setPromoApplying = (isApplying: boolean) => {
    _promo = { ..._promo, isApplying };

    notify();
  };

  // --- Credit ---

  const setCredit = (amount: number) => {
    _credit = { ..._credit, amount };
    notify();
  };

  const toggleCredit = () => {
    _credit = { ..._credit, isUsed: !_credit.isUsed };
    notify();
  };

  // --- Summary ---

  const setSummaryLoading = () => {
    _summary = { ..._summary, loading: true, error: false };
    notify();
  };

  const setSummaryData = (data: TCheckoutSummaryData) => {
    _summary = { data, loading: false, error: false };
    notify();
  };

  const resetSummary = () => {
    _summary = {
      data: undefined,
      loading: false,
      error: false,
    };
    notify();
  };

  const setSummaryError = (message: string) => {
    _summary = { ..._summary, loading: false, error: message };
    notify();
  };

  // --- Promo fetch guard ---

  /** Arms the one-shot skip flag. Call this before an action that will trigger
   *  its own summary fetch so the reactive fetch watcher doesn't fire twice. */
  const setSkipNextSummaryFetch = () => {
    _skipNextFetch = true;
  };

  /** Consumes the skip flag. Returns `true` once (and resets the flag), then
   *  `false` for every subsequent call until `setSkipNextSummaryFetch` is
   *  called again. */
  const shouldSkipNextSummaryFetch = () => {
    if (_skipNextFetch) {
      _skipNextFetch = false;
      return true;
    }
    return false;
  };

  // --- State access ---

  /**
   * Returns the current state snapshot. Uses the cached object when available
   * so callers that read state synchronously in the same tick get a stable
   * reference instead of a freshly allocated object.
   */
  const getState = (): TCartServiceState => {
    return _cachedState ?? buildState();
  };

  /**
   * Subscribes to state changes. Compatible with `useSyncExternalStore` —
   * returns an unsubscribe function.
   */
  const subscribe = (listener: (state: TCartServiceState) => void) => {
    subject.addObserver(listener);
    return () => subject.removeObserver(listener);
  };

  return {
    // Catalog
    setCatalog,
    repriceCart,
    addCatalogPlan,
    removeCatalogPlan,
    addCatalogAddOn,
    removeCatalogAddOn,
    // Cart
    addItem,
    removeItem,
    updateItemQuantity,
    updateCart,
    clearCart,
    reset,
    setOwnedPlanIds,
    // Promo
    setPromoInput,
    applyPromoCode,
    removePromoCode,
    setPromoApplying,
    // Credit
    setCredit,
    toggleCredit,
    // Summary
    setSummaryLoading,
    setSummaryData,
    resetSummary,
    setSummaryError,
    setSkipNextSummaryFetch,
    shouldSkipNextSummaryFetch,
    // State
    getState,
    subscribe,
  };
}

export const CartService = createCartService();
